"use client"

// components/tracker/digest-next/use-wheel-motion.ts
//
// The wheel's motion (FE-DIGEST-1 Phase D3b2, round 1), ported from the HTML
// prototype Oliver approved (complyrx-wheel-paper.html:786-875).
//
// THE SPLIT. The selected type is React state in the card and drives the
// detail column, the tab attributes and the gradient. This hook owns only
// the wheel's fractional position: position, velocity and target live in
// refs, and one animation-frame loop writes the pills' and ticks' styles
// directly, as the prototype's `draw` does. The card is never re-rendered
// per frame.
//
// CONSTANTS, each from the prototype unless named:
//   spring stiffness 160, damping exp(-19 * dt), dt capped at 0.033 s,
//   settle when |target - position| < 0.0004 and |velocity| < 0.002 (:830-832)
//   pill fade max(0.4, 1 - 0.2 * distance) (:808), inert beyond three places (:815)
//   ticks: 60 on a ring about (640, 270), radii 300 and 312, four ticks per
//   step, white within 0.23 rad, the pointer's own tick hidden (:820-826)
//   drag: starts past 5 px, 110 px per step, momentum (speed over max(8 ms,
//   dt)), release at position - momentum * 0.18, cancel without momentum (:849-866)
//   mouse wheel: line x16, page x600, reset after 180 ms, step at 35 px,
//   lock 330 ms, ctrl ignored (:842-847)
// and two of Paper's (2O80-0 spokes): grey ticks only up to six places from
// the pointer (the prototype draws seven, ten grey ticks at rest, Paper
// eight), and the pointer drawn static by the card.
//
// MOUSE WHEEL, Oliver's answer "Pills only" (20:50Z): the listener steps the
// types only over the pill zone (x 520 and right in the stage), and only
// calls preventDefault when a step is possible in that direction, so at the
// first and last type the page scrolls on.
//
// FLOATING PRESENTATION (FE-LANDING-1 port P3, Oliver 17:50Z). The homepage
// hero passes `wheelSteps: false` and `tickStyle: "floating"`. The first
// attaches no wheel listener at all, so the page always scrolls; drag and
// the card's click and keys still select. The second draws Paper GUF-0's
// on-dark spokes: every visible tick white and 4 wide, with Paper's alpha
// by angle (full within 0.23 rad, then 0.6, 0.4, 0.25 and 0.12 at 18, 24,
// 30 and 36 degrees). The Digest page passes neither and is unchanged.

import { useCallback, useEffect, useLayoutEffect, useRef } from "react"

// ---- The slot table (prototype :790-800, Paper for -3 to +1) ----

/** left, top, height, padding left, padding right, gap, tile, tile radius.
 *  Index 0 is offset -4, index 8 is offset +4. -3 to +1 are Paper's
 *  (2L4V-0 to 2L5O-0), +2 and +3 mirror -2 and -3 around y 270, -4 and +4
 *  continue the last step off the card. */
export const WHEEL_SLOTS: ReadonlyArray<
  readonly [number, number, number, number, number, number, number, number]
> = [
  [520, -46, 44, 5, 20, 10, 32, 8],
  [560, 16, 44, 5, 20, 10, 32, 8],
  [600, 78, 48, 6, 24, 12, 36, 9],
  [640, 148, 52, 8, 28, 12, 40, 10],
  [632, 240, 60, 10, 20, 12, 40, 10],
  [636, 338, 52, 8, 28, 12, 40, 10],
  [600, 414, 48, 6, 24, 12, 36, 9],
  [560, 480, 44, 5, 20, 10, 32, 8],
  [520, 546, 44, 5, 20, 10, 32, 8],
]

export type WheelSlot = {
  left: number
  top: number
  height: number
  padLeft: number
  padRight: number
  gap: number
  tile: number
  tileRadius: number
}

/** The slot for a fractional offset, interpolated between table rows, as
 *  the prototype's slotAt. Integer offsets return the row exactly. */
export function wheelSlotAt(offset: number): WheelSlot {
  const k = Math.max(-4, Math.min(4, offset)) + 4
  const lo = Math.floor(k)
  const hi = Math.min(8, lo + 1)
  const t = k - lo
  const a = WHEEL_SLOTS[lo]
  const b = WHEEL_SLOTS[hi]
  const v = a.map((value, j) => (t === 0 ? value : value + (b[j] - value) * t))
  return {
    left: v[0],
    top: v[1],
    height: v[2],
    padLeft: v[3],
    padRight: v[4],
    gap: v[5],
    tile: v[6],
    tileRadius: v[7],
  }
}

/** Opacity by distance from the pointer: 1, 0.8, 0.6, 0.4 (Oliver's fade). */
export function wheelFade(distance: number): number {
  return Math.max(0.4, 1 - 0.2 * distance)
}

// ---- Ticks (prototype :820-826, rest state equal to Paper's spokes) ----

export const TICK_COUNT = 60
const TICK_CX = 640
const TICK_CY = 270
const TICK_R1 = 300
const TICK_R2 = 312
/** Grey ticks show up to 0.68 rad (39 degrees) from the pointer: six ticks
 *  each side at rest, Paper's eight grey with the four white. */
const TICK_VISIBLE = 0.68
const TICK_WHITE = 0.23
const TICK_HIDDEN = 0.012

export type TickGeometry = {
  x1: number
  y1: number
  x2: number
  y2: number
  white: boolean
  visible: boolean
  /** Distance from the pointer, radians. */
  angle: number
}

/** "card": the Digest's white and grey ticks. "floating": Paper GUF-0's
 *  white ticks with alpha by angle, for the homepage hero. */
export type TickStyle = "card" | "floating"

/** Paper GUF-0's tick alpha at 18, 24, 30 and 36 degrees, interpolated so
 *  the ring stays smooth while it turns. */
const FLOATING_ALPHA: ReadonlyArray<readonly [number, number]> = [
  [TICK_WHITE, 1],
  [(18 * Math.PI) / 180, 0.6],
  [(24 * Math.PI) / 180, 0.4],
  [(30 * Math.PI) / 180, 0.25],
  [(36 * Math.PI) / 180, 0.12],
  [TICK_VISIBLE, 0.12],
]

export function floatingTickAlpha(angle: number): number {
  if (angle < TICK_WHITE) return 1
  for (let i = 1; i < FLOATING_ALPHA.length; i++) {
    const [a1, v1] = FLOATING_ALPHA[i]
    if (angle <= a1) {
      const [a0, v0] = FLOATING_ALPHA[i - 1]
      return v0 + ((v1 - v0) * (angle - a0)) / (a1 - a0)
    }
  }
  return FLOATING_ALPHA[FLOATING_ALPHA.length - 1][1]
}

/** A tick's stroke, width and opacity for a style. */
export function tickPaint(g: TickGeometry, style: TickStyle): { stroke: string; width: number; opacity: number } {
  if (style === "floating") {
    return { stroke: "#FFFFFF", width: 4, opacity: g.visible ? floatingTickAlpha(g.angle) : 0 }
  }
  return { stroke: g.white ? "#FFFFFF" : "#E1E7EF", width: g.white ? 3 : 2, opacity: g.visible ? 1 : 0 }
}

/** One tick's geometry for a wheel position. */
export function tickAt(i: number, position: number): TickGeometry {
  const angle = ((i + position * 4) / TICK_COUNT) * Math.PI * 2
  const c = Math.cos(angle)
  const s = Math.sin(angle)
  const a = Math.abs(Math.atan2(s, c))
  return {
    x1: TICK_CX + TICK_R1 * c,
    y1: TICK_CY + TICK_R1 * s,
    x2: TICK_CX + TICK_R2 * c,
    y2: TICK_CY + TICK_R2 * s,
    white: a < TICK_WHITE,
    visible: a < TICK_VISIBLE && a >= TICK_HIDDEN,
    angle: a,
  }
}

// ---- Styles written each frame ----

function writePill(el: HTMLElement, offset: number, selected: boolean) {
  const s = wheelSlotAt(offset)
  const distance = Math.abs(offset)
  const st = el.style
  st.transform = `translate3d(${s.left.toFixed(3)}px, ${s.top.toFixed(3)}px, 0)`
  st.height = `${s.height.toFixed(3)}px`
  st.paddingLeft = `${s.padLeft.toFixed(3)}px`
  st.paddingRight = `${s.padRight.toFixed(3)}px`
  st.gap = `${s.gap.toFixed(3)}px`
  st.setProperty("--dn-wheel-tile", `${s.tile.toFixed(3)}px`)
  st.setProperty("--dn-wheel-tile-radius", `${s.tileRadius.toFixed(3)}px`)
  st.setProperty("--dn-wheel-fade", selected ? "1" : wheelFade(distance).toFixed(3))
  st.zIndex = String(10 - Math.round(distance))
}

function writeTick(el: SVGLineElement, g: TickGeometry, style: TickStyle) {
  const paint = tickPaint(g, style)
  el.setAttribute("x1", g.x1.toFixed(2))
  el.setAttribute("y1", g.y1.toFixed(2))
  el.setAttribute("x2", g.x2.toFixed(2))
  el.setAttribute("y2", g.y2.toFixed(2))
  el.setAttribute("stroke", paint.stroke)
  el.setAttribute("stroke-width", String(paint.width))
  el.setAttribute("stroke-opacity", style === "card" ? String(paint.opacity) : paint.opacity.toFixed(3))
}

/** The rest style a pill renders with on the server and first paint, so the
 *  loop only takes over from the same values. */
export function pillRestStyle(offset: number, selected: boolean): Record<string, string> {
  const s = wheelSlotAt(offset)
  return {
    transform: `translate3d(${s.left}px, ${s.top}px, 0)`,
    height: `${s.height}px`,
    paddingLeft: `${s.padLeft}px`,
    paddingRight: `${s.padRight}px`,
    gap: `${s.gap}px`,
    "--dn-wheel-tile": `${s.tile}px`,
    "--dn-wheel-tile-radius": `${s.tileRadius}px`,
    "--dn-wheel-fade": selected ? "1" : String(wheelFade(Math.abs(offset))),
    zIndex: String(10 - Math.round(Math.abs(offset))),
  }
}

// ---- The hook ----

/** The pill zone starts at the leftmost slot (x 520 in the stage). */
const ZONE_LEFT = 520

/** Mouse wheel at the first or last type: false lets the page scroll on at
 *  once (current). true keeps consuming the gesture until the 330ms lock
 *  after a step has expired, so one flick that reaches the end does not
 *  also scroll the page. Oliver to decide. */
const WHEEL_HOLD_AT_ENDS_DURING_LOCK = false

type DragState = {
  id: number
  startX: number
  startY: number
  start: number
  lastY: number
  lastTime: number
  speed: number
  moved: boolean
  /** The element under the press, recorded before any pointer capture
   *  (Operational Gotchas: capture retargets pointerup). */
  downTarget: EventTarget | null
}

export type WheelMotion = {
  /** Callback refs for the five pills, by wheel index. */
  pillRef: (index: number) => (el: HTMLButtonElement | null) => void
  /** Callback refs for the sixty tick lines. */
  tickRef: (index: number) => (el: SVGLineElement | null) => void
  /** Callback ref for the stage that holds the pills and ticks. */
  stageRef: (el: HTMLElement | null) => void
  /** True from a drag's release until the click it produces has passed. */
  suppressClick: () => boolean
}

export function useWheelMotion({
  count,
  selectedIndex,
  reduced,
  onDragSelect,
  wheelSteps = true,
  tickStyle = "card",
}: {
  count: number
  selectedIndex: number
  reduced: boolean
  /** Called when a drag or the mouse wheel moves the selection. */
  onDragSelect: (index: number) => void
  /** False: no mouse-wheel listener, the page always scrolls (floating). */
  wheelSteps?: boolean
  /** How the ticks are painted each frame. */
  tickStyle?: TickStyle
}): WheelMotion {
  const pills = useRef<Array<HTMLButtonElement | null>>([])
  const ticks = useRef<Array<SVGLineElement | null>>([])
  const stage = useRef<HTMLElement | null>(null)
  const position = useRef(selectedIndex)
  const target = useRef(selectedIndex)
  const velocity = useRef(0)
  const raf = useRef(0)
  const lastFrame = useRef(0)
  const selected = useRef(selectedIndex)
  const drag = useRef<DragState | null>(null)
  const suppress = useRef(false)
  const reducedRef = useRef(reduced)
  const onSelectRef = useRef(onDragSelect)
  const wheel = useRef({ sum: 0, last: 0, lock: 0 })

  useLayoutEffect(() => {
    onSelectRef.current = onDragSelect
    reducedRef.current = reduced
  })

  const clamp = useCallback((n: number) => Math.max(0, Math.min(count - 1, n)), [count])

  const draw = useCallback(() => {
    const p = position.current
    pills.current.forEach((el, i) => {
      if (el) writePill(el, i - p, i === selected.current)
    })
    ticks.current.forEach((el, i) => {
      if (el) writeTick(el, tickAt(i, p), tickStyle)
    })
  }, [tickStyle])

  const frame = useCallback(
    (now: number) => {
      const dt = Math.min((now - (lastFrame.current || now - 16.67)) / 1000, 0.033)
      lastFrame.current = now
      velocity.current += (target.current - position.current) * 160 * dt
      velocity.current *= Math.exp(-19 * dt)
      position.current += velocity.current * dt
      if (
        Math.abs(target.current - position.current) < 0.0004 &&
        Math.abs(velocity.current) < 0.002
      ) {
        // Snap exactly onto the slot values, so rest equals D3b1 to the pixel.
        position.current = target.current
        velocity.current = 0
        raf.current = 0
        lastFrame.current = 0
        draw()
        return
      }
      draw()
      raf.current = requestAnimationFrame(frame)
    },
    [draw],
  )

  const animateTo = useCallback(
    (value: number) => {
      target.current = clamp(value)
      if (reducedRef.current) {
        cancelAnimationFrame(raf.current)
        raf.current = 0
        position.current = target.current
        velocity.current = 0
        lastFrame.current = 0
        draw()
      } else if (position.current === target.current && velocity.current === 0) {
        // Already at rest there: draw once, no loop.
        cancelAnimationFrame(raf.current)
        raf.current = 0
        lastFrame.current = 0
        draw()
      } else {
        // (Re)request the next frame. A frame requested while the page was
        // hidden may never be delivered, so a pending id is not trusted;
        // a stale lastFrame only costs one capped dt.
        cancelAnimationFrame(raf.current)
        raf.current = requestAnimationFrame(frame)
      }
    },
    [clamp, draw, frame],
  )

  // A selection made by keyboard, click or the stepper springs to it. A
  // selection made by a drag in progress does not: the pointer leads.
  useEffect(() => {
    selected.current = selectedIndex
    if (drag.current?.moved) {
      draw()
      return
    }
    animateTo(selectedIndex)
  }, [selectedIndex, animateTo, draw])

  // Reduced motion switched on mid-spring: jump to rest.
  useEffect(() => {
    if (reduced) animateTo(selected.current)
  }, [reduced, animateTo])

  useEffect(() => () => cancelAnimationFrame(raf.current), [])

  // Stable callback refs, one per pill and per tick, so React does not
  // detach and reattach them on every render.
  const pillRefs = useRef<Array<(el: HTMLButtonElement | null) => void>>([])
  const tickRefs = useRef<Array<(el: SVGLineElement | null) => void>>([])
  const pillRef = useCallback((index: number) => {
    pillRefs.current[index] ??= (el: HTMLButtonElement | null) => {
      pills.current[index] = el
    }
    return pillRefs.current[index]
  }, [])
  const tickRef = useCallback((index: number) => {
    tickRefs.current[index] ??= (el: SVGLineElement | null) => {
      ticks.current[index] = el
    }
    return tickRefs.current[index]
  }, [])

  // Pointer and wheel listeners on the stage, attached through the callback
  // ref so a remounted stage is always the one listened to.
  const detach = useRef<(() => void) | null>(null)
  const stageRef = useCallback(
    (el: HTMLElement | null) => {
      detach.current?.()
      detach.current = null
      stage.current = el
      if (!el) return

      const inZone = (clientX: number) => clientX - el.getBoundingClientRect().left >= ZONE_LEFT

      const onWheel = (e: WheelEvent) => {
        if (e.ctrlKey || !inZone(e.clientX)) return
        const dir = Math.sign(e.deltaY)
        if (dir === 0) return
        const next = selected.current + dir
        const now = performance.now()
        // At the first or last type the page scrolls on (unless the hold
        // flag above is on and a step's lock is still running).
        if (next < 0 || next > count - 1) {
          if (WHEEL_HOLD_AT_ENDS_DURING_LOCK && now < wheel.current.lock) e.preventDefault()
          return
        }
        e.preventDefault()
        const w = wheel.current
        if (now - w.last > 180) w.sum = 0
        w.last = now
        if (now < w.lock) return
        w.sum += e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 600 : 1)
        if (Math.abs(w.sum) >= 35) {
          const step = clamp(selected.current + Math.sign(w.sum))
          w.sum = 0
          w.lock = now + 330
          if (step !== selected.current) onSelectRef.current(step)
        }
      }

      const onDown = (e: PointerEvent) => {
        if (e.button !== 0 || !inZone(e.clientX)) return
        drag.current = {
          id: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          start: position.current,
          lastY: e.clientY,
          lastTime: performance.now(),
          speed: 0,
          moved: false,
          downTarget: e.target,
        }
        suppress.current = false
      }

      const onMove = (e: PointerEvent) => {
        const d = drag.current
        if (!d || e.pointerId !== d.id) return
        const dy = e.clientY - d.startY
        const dx = e.clientX - d.startX
        if (!d.moved && Math.hypot(dx, dy) > 5) {
          d.moved = true
          // Capture only once the drag has started; the press target was
          // recorded above, before this.
          try {
            el.setPointerCapture(e.pointerId)
          } catch {
            // A pointer the browser no longer tracks cannot be captured; the
            // drag still works through the window's pointerup listener.
          }
          el.dataset.dragging = "true"
          cancelAnimationFrame(raf.current)
          raf.current = 0
          velocity.current = 0
          lastFrame.current = 0
        }
        if (!d.moved) return
        e.preventDefault()
        const now = performance.now()
        d.speed = (e.clientY - d.lastY) / Math.max(8, now - d.lastTime)
        d.lastY = e.clientY
        d.lastTime = now
        position.current = clamp(d.start - dy / 110)
        target.current = position.current
        const nearest = clamp(Math.round(position.current))
        if (nearest !== selected.current) {
          selected.current = nearest
          onSelectRef.current(nearest)
        }
        draw()
      }

      const onEnd = (e: PointerEvent) => {
        const d = drag.current
        if (!d || e.pointerId !== d.id) return
        const moved = d.moved
        const momentum = e.type === "pointercancel" ? 0 : d.speed
        drag.current = null
        delete el.dataset.dragging
        if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId)
        if (!moved) return
        suppress.current = true
        const next = clamp(Math.round(position.current - momentum * 0.18))
        if (next !== selected.current) {
          selected.current = next
          onSelectRef.current(next)
        }
        animateTo(next)
        window.setTimeout(() => {
          suppress.current = false
        }, 0)
      }

      const onLost = (e: PointerEvent) => {
        if (drag.current) onEnd(e)
      }

      if (wheelSteps) el.addEventListener("wheel", onWheel, { passive: false })
      el.addEventListener("pointerdown", onDown)
      el.addEventListener("pointermove", onMove)
      el.addEventListener("pointerup", onEnd)
      el.addEventListener("pointercancel", onEnd)
      el.addEventListener("lostpointercapture", onLost)
      window.addEventListener("pointerup", onEnd)
      detach.current = () => {
        el.removeEventListener("wheel", onWheel)
        el.removeEventListener("pointerdown", onDown)
        el.removeEventListener("pointermove", onMove)
        el.removeEventListener("pointerup", onEnd)
        el.removeEventListener("pointercancel", onEnd)
        el.removeEventListener("lostpointercapture", onLost)
        window.removeEventListener("pointerup", onEnd)
      }
    },
    [animateTo, clamp, count, draw, wheelSteps],
  )

  useEffect(() => () => detach.current?.(), [])

  const suppressClick = useCallback(() => suppress.current, [])

  return { pillRef, tickRef, stageRef, suppressClick }
}
